import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useWatchList } from "../lib/useWatchList";
import { addRider, removeEvent, removeRider } from "../lib/watchlist";
import { getEventDetail } from "../lib/api";

export default function Watch() {
  const watch = useWatchList();
  const navigate = useNavigate();
  const [newRider, setNewRider] = useState("");
  const [eventNames, setEventNames] = useState<Map<string, string>>(new Map());

  const close = () =>
    window.history.length > 1 ? navigate(-1) : navigate("/");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const id of watch.events) {
        if (eventNames.has(id)) continue;
        try {
          const { event } = await getEventDetail(id);
          if (cancelled) return;
          setEventNames((prev) => {
            if (prev.has(id)) return prev;
            const next = new Map(prev);
            next.set(id, event.name);
            return next;
          });
        } catch {
          /* leave the raw id on failure */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch.events.join(",")]);

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRider.trim()) return;
    addRider(newRider);
    setNewRider("");
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-20" onClick={close} />
      <div
        className="fixed inset-x-0 bottom-0 sm:inset-auto sm:top-12 sm:left-1/2 sm:-translate-x-1/2 sm:w-[28rem] z-30 max-h-[85vh] overflow-y-auto bg-pit-bg border-t border-pit-border sm:border sm:rounded-xl rounded-t-xl pit-panel pb-[env(safe-area-inset-bottom)] relative"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="sticky top-0 bg-pit-bg/95 backdrop-blur border-b border-pit-border flex items-center justify-between px-4 py-3 z-10">
          <h1 className="text-lg font-mono font-bold">Watch list</h1>
          <button
            onClick={close}
            aria-label="Close"
            className="w-11 h-11 flex items-center justify-center rounded-md text-pit-muted hover:text-pit-text hover:bg-pit-border/40 text-xl"
          >
            ✕
          </button>
        </div>

        <div className="px-4 py-4 space-y-6">
          <p className="text-base text-pit-muted font-mono">
            Riders are matched as case-insensitive substrings against names in the results
            (RaceDay formats names as <span className="text-pit-text">LAST, First</span>). Watched events show up on the home page.
          </p>

          <section className="space-y-2.5">
            <h2 className="text-base font-mono font-bold uppercase tracking-wider text-pit-muted">
              Riders ({watch.riders.length})
            </h2>
            <form onSubmit={onAdd} className="flex gap-2">
              <input
                value={newRider}
                onChange={(e) => setNewRider(e.target.value)}
                placeholder="e.g. Robinson"
                className="flex-1 px-3 py-2 rounded-md border border-pit-border bg-pit-bg text-base font-mono focus:outline-none focus:border-pit-accent"
              />
              <button type="submit" className="btn btn-primary">
                add
              </button>
            </form>
            {watch.riders.length === 0 ? (
              <div className="text-base text-pit-muted font-mono italic">No riders yet.</div>
            ) : (
              <ul className="rounded-md border border-pit-border pit-panel divide-y divide-pit-border">
                {watch.riders.map((rider) => (
                  <li
                    key={rider}
                    className="flex items-center justify-between pl-3 pr-1 py-1 font-mono text-base"
                  >
                    <span className="truncate">{rider}</span>
                    <button
                      aria-label={`Remove ${rider}`}
                      className="w-11 h-11 flex items-center justify-center text-pit-muted hover:text-pit-accent text-lg flex-shrink-0"
                      onClick={() => removeRider(rider)}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="space-y-2.5">
            <h2 className="text-base font-mono font-bold uppercase tracking-wider text-pit-muted">
              Events ({watch.events.length})
            </h2>
            {watch.events.length === 0 ? (
              <div className="text-base text-pit-muted font-mono italic">
                No events watched.{" "}
                <Link to="/events" className="text-pit-accent hover:underline">
                  Browse events
                </Link>{" "}
                and tap the ☆ button on any event to watch it.
              </div>
            ) : (
              <ul className="rounded-md border border-pit-border pit-panel divide-y divide-pit-border">
                {watch.events.map((eventId) => {
                  const name = eventNames.get(eventId);
                  return (
                    <li
                      key={eventId}
                      className="flex items-center justify-between pl-3 pr-1 py-1 font-mono text-base gap-2"
                    >
                      <Link
                        to={`/event/${eventId}`}
                        className="hover:text-pit-accent min-w-0 flex-1 py-1"
                        title={eventId}
                      >
                        {name ? (
                          <>
                            <div className="truncate">{name}</div>
                            <div className="text-[10px] text-pit-muted truncate">
                              {eventId}
                            </div>
                          </>
                        ) : (
                          <span className="truncate">{eventId}</span>
                        )}
                      </Link>
                      <button
                        aria-label={`Remove ${name ?? eventId}`}
                        className="w-11 h-11 flex items-center justify-center text-pit-muted hover:text-pit-accent text-lg flex-shrink-0"
                        onClick={() => removeEvent(eventId)}
                      >
                        ✕
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
